import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import { cn } from 'src/lib/utils';

// Three independently-scrollable columns — hour / minute / AM-PM — for
// DateTimePicker.jsx's time popover, replacing ClockDial's analog face per
// an explicit reference screenshot (a compact "wheel"-style picker, not a
// clock). Minutes are offered in 15-minute stops (00/15/30/45), matching
// that reference exactly — a further precision reduction from ClockDial's
// own 5-minute stops (itself already a reduction from the original
// free-typed-any-minute input), specifically requested via this reference.
//
// Unlike ClockDial's two-step hour-then-minute flow, all three columns
// show at once here — the reference has no "face" to advance through, so
// there's no step state to track; each column just commits its own value
// independently the moment it's clicked.
const HOUR_ITEMS = Array.from({ length: 12 }, (_, i) => i + 1); // 1..12
const MINUTE_ITEMS = [0, 15, 30, 45];
const PERIOD_ITEMS = ['AM', 'PM'];
// ~4.5 rows visible at once, matching the reference's own cropped scroll
// window — the rest of each list scrolls into view.
const COLUMN_HEIGHT = 168;

const pad2 = (n) => String(n).padStart(2, '0');

const TimeColumn = ({
  items,
  selectedValue,
  formatLabel,
  onSelect,
  ariaLabel,
  disabled,
  center,
}) => {
  const selectedRef = useRef(null);
  const listRef = useRef(null);

  // Scrolls the current selection into view once, when this column first
  // mounts — i.e. every time the popover opens (PopoverContent unmounts on
  // close) — not on every value change, so picking a new value doesn't
  // yank the scroll position around while the user is still browsing.
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ block: 'center' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // This picker opens from inside a Radix Dialog (ActionPayloadModal) via a
  // nested Popover — Dialog's react-remove-scroll body-lock preventDefault's
  // any wheel event over a descendant it hasn't specifically allow-listed,
  // and Popover.Content doesn't opt into that allow-list. Same root cause,
  // same fix as AsyncMultiSelectDropdown.jsx's own handleWheel (see its
  // comment for the full story): write scrollTop directly, since that isn't
  // the "default action" a preventDefault'd wheel event suppresses.
  const handleWheel = (e) => {
    const el = listRef.current;
    if (!el) return;
    el.scrollTop += e.deltaY;
  };

  return (
    <div
      ref={listRef}
      role="listbox"
      aria-label={ariaLabel}
      onWheel={handleWheel}
      className={cn(
        'flex w-14 shrink-0 flex-col gap-0.5 overflow-y-auto px-1 py-1',
        // Only meaningful for a short, non-scrolling list (Meridiem's 2
        // items) — Hour/Minute stay top-aligned since they scroll.
        center && 'justify-center'
      )}
      style={{ height: COLUMN_HEIGHT }}
    >
      {items.map((item) => {
        const selected = item === selectedValue;
        return (
          <button
            key={item}
            ref={selected ? selectedRef : null}
            type="button"
            role="option"
            aria-selected={selected}
            disabled={disabled}
            onClick={() => onSelect(item)}
            className={cn(
              'shrink-0 rounded-md py-1.5 text-center text-sm tabular-nums transition-colors disabled:cursor-not-allowed disabled:opacity-50',
              selected
                ? 'bg-primary font-semibold text-primary-foreground'
                : 'text-foreground hover:bg-muted'
            )}
          >
            {formatLabel(item)}
          </button>
        );
      })}
    </div>
  );
};

TimeColumn.propTypes = {
  items: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  ).isRequired,
  selectedValue: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  formatLabel: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
  center: PropTypes.bool,
};

TimeColumn.defaultProps = {
  selectedValue: null,
  disabled: false,
  center: false,
};

const TimeColumnPicker = ({
  hour,
  minute,
  period,
  onSelectHour,
  onSelectMinute,
  onSelectPeriod,
  onClose,
  disabled,
}) => (
  <div className="relative flex divide-x divide-border">
    <TimeColumn
      items={HOUR_ITEMS}
      selectedValue={hour ? Number(hour) : null}
      formatLabel={pad2}
      onSelect={onSelectHour}
      ariaLabel="Hour"
      disabled={disabled}
    />
    <TimeColumn
      items={MINUTE_ITEMS}
      selectedValue={minute !== '' ? Number(minute) : null}
      formatLabel={pad2}
      onSelect={onSelectMinute}
      ariaLabel="Minute"
      disabled={disabled}
    />
    <TimeColumn
      items={PERIOD_ITEMS}
      selectedValue={period}
      formatLabel={(p) => p}
      onSelect={onSelectPeriod}
      ariaLabel="Meridiem"
      disabled={disabled}
      center
    />
    {onClose && (
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute -right-1 -top-1 rounded-lg p-0.5 text-muted transition-colors !border-none bg-gray-300 hover:bg-muted hover:text-gray-500 dark:bg-gray-700 dark:hover:bg-muted-dark dark:hover:text-gray-400"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    )}
  </div>
);

TimeColumnPicker.propTypes = {
  // '1'-'12' or '' (nothing picked yet).
  hour: PropTypes.string,
  // '00'-'59' or '' — only ever one of MINUTE_ITEMS' values in practice
  // once picked here, but reads whatever DateTimePicker's own local state
  // carries.
  minute: PropTypes.string,
  period: PropTypes.oneOf(['AM', 'PM']),
  onSelectHour: PropTypes.func.isRequired,
  onSelectMinute: PropTypes.func.isRequired,
  onSelectPeriod: PropTypes.func.isRequired,
  // Optional — renders a close (X) button in the top-right corner when
  // given, so the popover can be dismissed without clicking outside it.
  onClose: PropTypes.func,
  disabled: PropTypes.bool,
};

TimeColumnPicker.defaultProps = {
  hour: '',
  minute: '',
  period: 'AM',
  onClose: undefined,
  disabled: false,
};

export default TimeColumnPicker;
